import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { CompanyAction } from '../../../actions/CompanyAction';
import { LicenseAction } from '../../../actions/LicenseAction';
import { buildSummaryBadges, computeCorpQuotaBreakdown, computeNodeQuotaBreakdown, extractAllocationFacts, resolveEffectiveCorpBudget, scopeOwnDealers } from '../../../lib/smartDentQuota';
import { computeNodeQuota, getDirectAllocationChildren, hasAllocationDescendants, sumSubtreeRedeemed } from '../../../lib/allocationGraph';
import { buildParentOf } from '../../../lib/allocationContext';
import { extractDealerIdFromSk, resolveOrgTypeLabel } from '../../../lib/licenseHelper';
import { ORG_HQ, ORG_SUBSIDIARY } from '../../../config/appConstants';
import { Translate as t } from '../../../config/Translate';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import AllocateDealerModal from './AllocateDealerModal';
import VoucherTemplateEditor from './VoucherTemplateEditor';
import AllocationHistoryTable from '../../../components/license/AllocationHistoryTable';
import PeriodFilter, { computeRange, FILTER_ROW_CLASS } from '../../../components/license/PeriodFilter';
import { renderHeader } from '../../../components/license/columns';
import CompanyCell from '../../../components/license/CompanyCell';
import LicenseListTabs from '../../../components/license/LicenseListTabs';
import { SUMMARY_BG, SUMMARY_HIGHLIGHT_BG } from '../../../components/license/summaryStyle';

const TAB_SUB_ORG = 'subOrgAllocations';
const TAB_TEMPLATE = 'voucherTemplate';

const LicenseSubsidiaryTab = ({
    user,
    role,
    selectedPackage,
    corpAllocation,
    dealerAllocations,
    myReceivedAllocation,
    dealerAllocationHistory,
    companies,
    allocationNodes,
    scopeContext,
    product: productProp,
}) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);
    const [allocateModal, setAllocateModal] = useState({ show: false, mode: 'allocate', record: null });
    // 현재 안쪽 탭 — 바우처 템플릿 탭이 열려 있는 동안만 편집기를 마운트한다(수치 띠는 탭과 무관하게 유지).
    const [activeTab, setActiveTab] = useState(TAB_SUB_ORG);
    const [historyLoading, setHistoryLoading] = useState(false);
    // 모든 Period 필터 기본값 'today' — 사용자 요청. 일별 운영 모니터링이 주 use case.
    const [historyFilter, setHistoryFilter] = useState({ preset: 'today', from: '', to: '' });

    useEffect(() => {
        if (role === ORG_SUBSIDIARY) dispatch(CompanyAction.GetCompany());
    }, [dispatch, role]);

    const historyRange = useMemo(() => computeRange(historyFilter), [historyFilter]);

    // FEAT-050 S5c: 새로고침 버튼과 mount 양쪽에서 재사용하기 위해 useCallback 추출.
    // BUG-210 리뷰(Major): allocate/revoke onSuccess 가 이 4개 조회를 별도로 다시 나열하면
    // 조회가 하나 늘 때마다 같은 결함(일부만 재조회)이 재발한다 — options 를 받아 그대로
    // 전달해 onSuccess 도 이 함수 하나만 호출하게 한다({ fresh: true }로 write-후 캐시 우회).
    const fetchAllocations = useCallback((options = {}) => {
        if (!selectedPackage?._id || !user?.companyId) return;
        const opts = { ...options, product };
        dispatch(SmartDentAction.GetCorpAllocation(selectedPackage._id, user.companyId, opts));
        dispatch(SmartDentAction.GetDealerAllocations(selectedPackage._id, user.companyId, opts));
        // 중간 법인은 자기 corp 원장이 비어있고 상위에서 받은 record 에 예산이 있음 — 병행 조회.
        dispatch(SmartDentAction.GetMyDealerAllocation(user.companyId, opts));
        // IMP-420: 노드 모델(N단계) 서브트리 조회 — 표시 소비처 전환 병행 배선.
        dispatch(SmartDentAction.GetAllocationSubtree(selectedPackage._id, user.companyId, opts));
    }, [dispatch, selectedPackage?._id, user?.companyId, product]);

    useEffect(() => {
        fetchAllocations();
    }, [fetchAllocations]);

    // FEAT-024 AC-7 — Sub-org ↔ Corp 할당/회수 이력 마운트 시 1회 페치.
    // Grant History UI 비노출로 GetGrantHistory 호출 제거.
    useEffect(() => {
        if (!user?.companyId) return;
        setHistoryLoading(true);
        Promise.resolve(
            dispatch(SmartDentAction.GetDealerAllocationHistory(user.companyId, null, { product })),
        ).finally(() => setHistoryLoading(false));
    }, [dispatch, user?.companyId, product]);

    const companyMap = useMemo(() => {
        const map = {};
        (companies || []).forEach((c) => { map[c.companyId] = c; });
        return map;
    }, [companies]);

    // BUG-105 (Jira RTSQA-336) — Allocation Summary Remaining = total - sum(dealer.allocated).
    // "New Allocation 으로 dealer 에 추가 부여 가능한 양" 의미. 본사 직접 redeem (u_*) 은 차감 안 함.
    // FEAT-050 S5c: 자기 corp 원장이 비면(중간 법인) 상위에서 받은 레코드를 예산으로 폴백.
    // BUG-177: SSoT 헬퍼(resolveEffectiveCorpBudget/scopeOwnDealers)로 통일 — AllocateDealerModal /
    // LicenseOverviewTab 과 동일 로직 공유(중복 방지). 판정·스코프 근거는 헬퍼 주석 참조.
    const { effectiveCorp, isSubEntity } = resolveEffectiveCorpBudget(corpAllocation, myReceivedAllocation, user?.companyId, null, scopeContext);
    const scopedDealers = scopeOwnDealers(dealerAllocations, user?.companyId, isSubEntity);
    // FEAT-050 (나) Increment 2 첫 슬라이스: 중간 법인(isSubEntity)은 자기 직접 redeem(u)도
    // 차감한 selfUsable(t-u-Σ자식.t)을 "진짜 배분 가능 잔여"로 표시. 최상위는 기존
    // allocatable(t-Σ자식.t) 유지(BUG-191 롤업 케이스 보호).
    const nodeQuota = computeNodeQuota(effectiveCorp, scopedDealers);
    // 라벨은 값을 고르는 이 분기와 같은 조건을 쓴다(아래 remainingIsSelfUsable).
    const legacyRemainingIsSelfUsable = isSubEntity;
    const legacyRemaining = legacyRemainingIsSelfUsable
        ? { newQty: nodeQuota.initial.selfUsable, renewQty: nodeQuota.renewal.selfUsable }
        : computeCorpQuotaBreakdown(effectiveCorp, scopedDealers).allocatable;

    // BUG-186: 목록은 Dealer 탭(LicenseDealerTab)과 동일하게 dealerAllocations 를 그대로
    // 표시(형제/부모 배분 제외는 조회 thunk GetDealerAllocations 책임으로 일원화).
    // remaining 은 자기 배분분만 차감(scopedDealers, own-scope 유지).
    // 주의: thunk(GetDealerAllocations, SmartDentAction.js:159)는 derive(isSubEntity) 경로에서
    // 자체 own-scope 필터를 적용하므로 이 슬롯이 이미 스코프됐을 수 있다. 본 변경의 목적은
    // 두 탭이 같은 슬롯을 컴포넌트 재필터 없이 렌더해 "표시 정합"을 보장하는 것(스코프 SSoT=thunk).
    // IMP-425 §12.1-A (PR #536 리뷰 Major 반영): 자기 행은 목록에서 제외하지 않는다 — 잔여
    // 계산(scopedDealers)이 자기 행을 포함하는 것과 동일 배열을 근거로 해야 캡션
    // `Total − Σ Allocated` 항등식이 유지되고, 옛 셀프딜링 잔재 행을 운영자가 발견할 수 있다.
    // 자기회수는 서버가 항상 거부(SELF_REVOKE_BLOCKED)하므로 행은 표시하되 Revoke 버튼만
    // 렌더하지 않는다(아래 렌더 루프 참조).
    const legacyListDealers = Array.isArray(dealerAllocations) ? dealerAllocations : [];

    // IMP-420 approach A: 노드 모델(N단계, allocationNodes) 우선 배선. 데이터 미도착/effectiveOwner
    // 해석 불가(마운트 직후, GetAllocationSubtree 응답 전 등)시 기존 2-tier 경로로 폴백해
    // 회귀를 최소화한다(점진 전환).
    const parentOf = useMemo(() => buildParentOf(companies), [companies]);
    const hasAllocationNodes = !!allocationNodes && Object.keys(allocationNodes).length > 0;
    const nodeBreakdown = useMemo(
        () => computeNodeQuotaBreakdown(user?.companyId, { nodesByCompanyId: allocationNodes, parentOf }),
        [user?.companyId, allocationNodes, parentOf],
    );
    const useNodeModel = hasAllocationNodes && !!nodeBreakdown.effectiveOwner;
    // IMP-420: 게이트 제거 — 흡수(buildNodesByCompanyId)가 corp 원장(2-seg)의 롤업 u/i 를
    // 버리고 셀프딜링만 흡수하도록 고쳐져 node.u 가 항상 "자기소모"가 된다. 따라서
    // selfUsable(t-u-Σ자식.t) 단일 공식이 최상위/중간법인 모두 정합(BUG-191 롤업 이중차감은
    // 흡수 단계에서 corp 원장 u 를 버려 해소).
    const remaining = useNodeModel
        ? { newQty: nodeBreakdown.initial.selfUsable, renewQty: nodeBreakdown.renewal.selfUsable }
        : legacyRemaining;
    // 노드 경로·legacy 중간법인 = Self-Usable(t−u−Σ자식.t), legacy 최상위 = Unallocated(t−Σdealer.t).
    const remainingIsSelfUsable = useNodeModel || legacyRemainingIsSelfUsable;
    const listDealers = useNodeModel
        ? getDirectAllocationChildren(user?.companyId, allocationNodes)
        : legacyListDealers;

    // IMP-427 (#368): Allocation Summary 원천 수치 배지. 표시 중인 remaining 과 **같은 원천**에서
    // 뽑는다(경로가 갈리면 배지와 잔여가 서로 다른 수치가 되어 화면이 어긋난다).
    const nodeOwner = nodeBreakdown.effectiveOwner;
    // [보안] PR #540 리뷰 Major 1·2 (CWE-200): `isSubEntity=false` 는 "최상위 법인"과 "부모와 mg 를
    // 공유하는 중간법인인데 받은 레코드를 못 찾음"을 구분하지 못한다. 후자에서 corpAllocation 은
    // 부모 mg 의 2-seg 원장이고(queryCorpAllocation(pkg, ownMg)), 그 t/u 는 부모+형제 전 조직의
    // 예산·리딤이다. 도달 경로는 (a) 부모 mg 소속 중간법인에 아직 할당 없음, (b) queryDealerAllocation
    // 일시 실패 시 read fallback `[]` → fail-open. 종전에는 이 레코드가 파생 remaining 계산에만
    // 쓰여 원값이 화면에 없었으나, 배지·참고 줄은 원값을 그대로 노출하므로 게이트가 필요하다.
    //
    // 게이트 = 원장 소유 확인. 조직 트리에서 자기 위에 부모가 있으면 최상위가 아니므로 원장이
    // 자기 것이라고 단정할 수 없다. 중간법인(isSubEntity)은 출처가 자기 received 레코드라 안전.
    // [보안 · PR #540 리뷰 Major] 이 게이트는 `parentOf`(= `companies` 조회 결과)에 의존한다.
    // `companies` 초기값은 `[]` 이고 `GetCompany()`(:75)는 `fetchAllocations()`(:84~96)와 **순서 보장
    // 없이 병렬**이다. corp 데이터가 먼저 도착해 `isSubEntity=false` 로 확정된 리렌더에서 `companies`
    // 가 아직 비면 `parentOf={}` → 게이트가 열려 **바로 그 fail-open 케이스가 노출**된다.
    // `GetCompany()` 실패 시엔 이 상태가 영속한다.
    // → 트리 데이터 도착 여부를 별도로 판정해 **미도착이면 fail-closed**(배지·참고 줄 숨김).
    //   판정 기준은 "자기 회사가 목록에 있는가" — `parentOf[self]` 가 의미를 갖는 최소 조건이며,
    //   `parentOf` 가 비어 있음(미도착)과 "부모 없음(최상위)"을 구분한다.
    const companyTreeReady = (companies || []).some((c) => c && c.companyId === user?.companyId);
    const ledgerMayNotBeMine = !isSubEntity && (!companyTreeReady || !!parentOf[user?.companyId]);
    const summaryFacts = ledgerMayNotBeMine
        ? null
        : (useNodeModel
            ? extractAllocationFacts(
                allocationNodes?.[nodeOwner],
                getDirectAllocationChildren(nodeOwner, allocationNodes),
            )
            : extractAllocationFacts(effectiveCorp, scopedDealers));
    // [중요] 리딤(u)의 스코프가 tier·경로별로 다르다.
    //  - 노드 경로: `buildNodesByCompanyId` 가 corp 원장(2-seg)의 롤업 u 를 버리므로 node.u 는
    //    항상 **자기소모**다. 표시 remaining(selfUsable = t−u−Σd)이 이 값을 차감한다.
    //  - legacy 폴백 + 최상위: remaining 이 allocatable(t−Σd)이라 u 를 **차감하지 않는다**.
    //    게다가 이때 effectiveCorp 는 corp 원장이므로 그 u 는 하위 조직까지 합친 조직 전체 롤업이다.
    //  - legacy 폴백 + 중간법인: effectiveCorp 가 받은 레코드(3-seg)라 u 는 자기 리딤이고,
    //    remaining(selfUsable)이 이를 차감한다.
    //
    // 따라서 리딤을 **배지(차감 항목) 위치에 두는 조건 = 표시 remaining 이 실제로 그 u 를
    // 차감할 때**로 한정한다. 그러지 않으면 운영자가 `Total − Allocated − Redeemed` 로 잘못
    // 뺄셈해 화면 Remaining 과 어긋난다(실측 t=10·Σd=4·자기소모 u=1 → Remaining 5. 조직 롤업 4 를 배지로 두면 10−4−4=2).
    // 차감하지 않는 조직 전체 롤업은 아래 참고 줄로 분리해 정보 자체는 유지한다.
    const remainingSubtractsRedeem = useNodeModel || isSubEntity;
    const summaryBadges = buildSummaryBadges(summaryFacts, { includeRedeemed: remainingSubtractsRedeem });
    // 조직 전체 리딤(자기 + 하위 전체) — 최상위 법인에서만 의미가 있다(중간법인의 3-seg u 는 자기
    // 리딤이라 서브트리 합과 같아진다). 위 보안 게이트를 동일 적용한다.
    //
    // PR #540 리뷰 Major 4: 종전에는 여기서 corp 원장의 롤업 u 를, 딜러 탭에서는 sumSubtreeRedeemed
    // 를 써 **같은 라벨에 두 정의**가 존재했다. 노드 서브트리 합산으로 단일화한다 — mg-null 중간법인이
    // 자식을 부모 mg 에 쓰는 3단 조직에서 원장 롤업과 갈리는 문제도 함께 해소된다.
    // (실측 검증: 원장 롤업 4/0 == 서브트리 합 4/0)
    // 딜러 탭과 동일 술어로 게이트해 하위가 없으면 노출하지 않는다(자기 리딤과 중복이므로).
    //
    // BUG-207 (#370): 종전에 `!isSubEntity` 조건이 있어 **중간 법인은 무조건 제외**됐다. 근거는
    // "중간 법인의 3-seg 레코드 u 는 자기 리딤이라 서브트리 합과 같아진다" 였으나 **하위 조직이
    // 있으면 서브트리 합 = 자기 + 하위 이므로 두 값이 다르다**(실측: RAY99902 자기 1 / 조직 전체 3).
    // 중복이 되는 조건은 hasAllocationDescendants 가 이미 처리하므로 tier 조건은 불필요하고,
    // 제거하면 딜러 탭과 술어가 완전히 대칭이 된다(같은 조직을 어느 탭에서 봐도 같은 규칙).
    const showOrgWideRedeemed = !ledgerMayNotBeMine
        && useNodeModel
        && hasAllocationDescendants(nodeOwner, allocationNodes);
    const orgWideRedeemed = showOrgWideRedeemed ? sumSubtreeRedeemed(nodeOwner, allocationNodes) : null;

    // Allocation History — FE-side 기간 필터 (timestamp 기준).
    // useMemo 제거하고 매 렌더 직접 계산 — 데이터 작고 staleness 위험 제거 우선.
    const filterHistoryByDate = (rows) => {
        if (historyRange.fromTs === undefined || historyRange.toTs === undefined) return rows || [];
        return (rows || []).filter((r) => {
            const ts = typeof r.ts === 'number'
                ? r.ts
                : (r.tsIso ? new Date(r.tsIso).getTime() : NaN);
            if (!Number.isFinite(ts)) return false;
            return ts >= historyRange.fromTs && ts <= historyRange.toTs;
        });
    };
    const filteredDealerAllocationHistory = filterHistoryByDate(dealerAllocationHistory);

    // IMP-191 follow-up (2026-05-12): HQ 도 자기 voucher template 편집 + 법인 자기 자신
    // 데이터 편집 권한 부여. Confluence "Local License Check API doc" v22 §"RAYGuard 에서
    // Voucher Template 확인 방법" — HQ 도 자기 vouchers:default 템플릿을 가진다.
    //
    // M2 (CWE-285 HQ silent expansion 차단): voucher template 편집 권한을 별도 변수로 분리.
    // canEdit 는 allocation UI(할당/회수/고객 라이선스 Actions) 전용 — 법인(Subsidiary)만 허용.
    // canEditVoucherTemplate 은 voucher template 탭 접근 전용 — 법인 + HQ 모두 허용.
    // 두 권한을 단일 변수로 묶으면 HQ 에게 allocation UI Actions 이 의도치 않게 열릴 위험.
    const canEdit = role === ORG_SUBSIDIARY;
    const canEditVoucherTemplate = role === ORG_SUBSIDIARY || role === ORG_HQ;

    return (
        <div>
            {/* 할당량 요약 — 전체 폭 한 줄 띠. 안쪽 탭은 탭 줄 아래만 바꾸므로 바우처 템플릿 탭에서도 그대로 둔다. */}
            {/* PR #217 Round 1 (#1): "Remaining" 단어가 Overview 와 같으면서도 다른 공식을
                사용하므로 의미 모호. 보조 텍스트로 정의 명시 → 회귀 가드 + 다음 개발자 인지. */}
            {(
                <div className="border rounded mb-2" style={{ background: SUMMARY_BG }} data-testid="license-subsidiary-summary">
                    <div className="d-flex flex-wrap align-items-stretch">
                        {/* IMP-427: 원천 수치 배지 — Remaining 앞에 배치해 "무엇에서 나온 잔여인지"
                            먼저 보여준다. 레코드 미도착이면 배열이 비어 렌더되지 않는다. */}
                        {summaryBadges.length > 0 && (
                            <div className="px-3 py-1 d-flex align-items-center" style={{ borderRight: '1px solid #dee2e6' }}>
                                <div className="d-flex flex-wrap gap-2" data-testid="license-subsidiary-summary-badges">
                                    {summaryBadges.map((b) => (
                                        <span
                                            key={b.key}
                                            className="badge bg-light text-dark border fw-normal"
                                            data-testid={`license-subsidiary-badge-${b.key}`}
                                        >
                                            {t(b.labelKey)} {b.qty.newQty ?? 0} / {b.qty.renewQty ?? 0}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="px-3 py-1" style={{ background: SUMMARY_HIGHLIGHT_BG, borderRight: '1px solid #dee2e6' }}>
                            <div className="text-muted small">
                                {remainingIsSelfUsable
                                    ? t('license.label.selfUsable')
                                    : t('license.label.unallocated')}
                            </div>
                            <div>
                                {t('license.label.initial')} <strong>{remaining.newQty}</strong> / {t('license.label.renewal')} <strong>{remaining.renewQty}</strong>
                            </div>
                            {/* FEAT-050 (나) Increment 2 Major: 이 캡션(t−Σd)은 최상위 법인의
                                allocatable 공식 설명이다. 중간 법인(isSubEntity)은 위 remaining 이
                                selfUsable(t−u−Σd)로 바뀌어 캡션과 어긋나므로 최상위에서만 노출.
                                IMP-420 M1: 노드 경로(useNodeModel)는 selfUsable 단일 공식이라
                                t−Σd 캡션은 부정확 → 숨김. legacy 폴백에서만 노출. */}
                            {!isSubEntity && !useNodeModel && (
                                <small className="text-muted d-block">
                                    {t('license.label.allocatableToSubOrgs')} (= {t('common.label.total')} − Σ {t('license.status.allocated')})
                                </small>
                            )}
                        </div>
                        {/* 동작 묶음: [새로고침][New Allocation] — 띠 마지막 칸 오른쪽 끝 */}
                        <div className="ms-auto px-3 py-1 d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ margin: 0 }}
                                data-testid="license-subsidiary-refresh-btn"
                                // BUG-210 리뷰 후속: fetchAllocations 가 이제 options 를 받으므로
                                // onClick 직결 시 클릭 이벤트 객체가 options 로 흘러든다(무해하나
                                // 의도와 다름) — 인자 없이 명시 호출.
                                onClick={() => fetchAllocations()}
                            >
                                {t('common.button.refresh')}
                            </button>
                            {canEdit && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    style={{ margin: 0 }}
                                    onClick={() => setAllocateModal({ show: true, mode: 'allocate', record: null })}
                                >
                                    {t('license.label.newAllocation')}
                                </button>
                            )}
                        </div>
                    </div>
                    {/* IMP-427: 조직 전체 리딤(원장 롤업) 참고 줄 — 하위 조직 리딤까지 합친
                        값이라 위 뺄셈에 넣으면 Remaining 과 어긋난다. 배지가 아닌 참고 줄로 둔다. */}
                    {orgWideRedeemed && (
                        <small className="text-muted d-block px-3 py-1 border-top" data-testid="license-subsidiary-redeemed-orgwide-ref">
                            {t('license.label.redeemedOrgWide')}: {t('license.label.initial')} {orgWideRedeemed.newQty ?? 0} / {t('license.label.renewal')} {orgWideRedeemed.renewQty ?? 0}
                        </small>
                    )}
                </div>
            )}

            {/* 목록 안쪽 탭 [하위 조직 할당] [할당 이력] [바우처 템플릿] (구역 전환 버튼 줄을 흡수).
                바우처 템플릿 탭은 법인·HQ(canEditVoucherTemplate)만 노출한다. */}
            <LicenseListTabs
                onChange={setActiveTab}
                tabs={[
                    {
                        key: TAB_SUB_ORG,
                        label: t('license.label.subOrgAllocations'),
                        content: (
                            <>
            <div className="table-responsive mb-2">
                <table className="table table-sm table-bordered">
                    <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                        <tr>
                            {renderHeader([
                                { key: 'company', label: 'common.label.company' },
                                { key: 'type', label: 'common.field.type' },
                                {
                                    key: 'allocated',
                                    title: 'license.label.totalAllocationBudget',
                                    node: (<>{t('license.status.allocated')}<br /><small className="text-muted">({t('license.label.initial')} / {t('license.label.renewal')})</small></>),
                                },
                                // IMP-159 (Jira RTSQA-337): Issued (i_*) 컬럼 숨김.
                                // SMARTDent over-issuance 정책상 i 는 t 초과 발급 가능해 사용자 혼란.
                                // BE 응답에는 i_* 그대로 유지. 필요 시 향후 toggle 또는 별도 화면에서 노출.
                                {
                                    key: 'used',
                                    title: 'license.label.vouchersRedeemed',
                                    node: (<>{t('common.status.used')}<br /><small className="text-muted">({t('license.label.initial')} / {t('license.label.renewal')})</small></>),
                                },
                                // 행 값은 selfUsable(t−u−Σ자식.t, 아래 remI/remR) → Self-Usable. 시각적 강조.
                                {
                                    key: 'remaining',
                                    title: 'license.label.allocationBudgetRemaining',
                                    className: 'bg-primary-subtle',
                                    node: (<><span className="fw-bold text-primary">{t('license.label.selfUsable')}</span><br /><small className="text-muted">({t('license.label.initial')} / {t('license.label.renewal')})</small></>),
                                },
                            ])}
                            {canEdit && <th>{t('common.label.actions')}</th>}
                        </tr>
                    </thead>
                    <tbody className="align-middle">
                        {listDealers.length === 0 ? (
                            <tr><td colSpan={canEdit ? 6 : 5} className="text-center text-muted">{t('common.message.noData')}</td></tr>
                        ) : (
                            listDealers.map((d) => {
                                const dealerId = extractDealerIdFromSk(d.sk);
                                const company = companyMap[dealerId];
                                // SMARTDent over-issuance 모델 — i 는 활성화 제약 없음(마케팅 차원).
                                // IMP-159 (Jira RTSQA-337): Issued(i_*) 셀은 혼란 방지 위해 제거.
                                // BUG-212: Remaining 은 naive(t-u) 가 아니라 selfUsable(t-u-Σ자식.t) —
                                // 이 행의 딜러(dealerId) 자신이 더 하위로 위임한 몫까지 반영해야 Dealer 탭
                                // 상세(LicenseDealerTab.js selectedNodeBreakdown, PR #540 Major 6)와 값이
                                // 일치한다. 같은 SSoT(computeNodeQuota, allocationGraph.js)로 정렬 — 하위가
                                // 없으면 Σ자식.t=0 이라 기존 naive 값과 동일해 무회귀.
                                // PR #549 리뷰 Major: 게이트는 행 출처(`listDealers`, :171 useNodeModel)와
                                // 반드시 같은 축이어야 한다. 더 넓은 hasAllocationNodes 를 쓰면 노드는
                                // 도착했으나 effectiveOwner 미해석(companies 미도착 등)이라 `d`가 legacy
                                // dealerAllocations 행인 구간에서, 무관한 allocationNodes 그래프의 Σ자식.t
                                // 를 그 legacy 행 t 에서 빼는 이종 모델 혼합이 발생한다 — 자기 잔재 행이면
                                // 자기 직속 자식 전체가 잘못 차감돼 Remaining 이 0 으로 눌릴 수 있다.
                                const dealerChildren = useNodeModel
                                    ? getDirectAllocationChildren(dealerId, allocationNodes)
                                    : [];
                                const dealerQuota = computeNodeQuota(d, dealerChildren);
                                // IMP-388: 개편 전 Dealer 탭 요약 카드가 보여주던 하위 배분(Σ직속자식.t)·조직 전체
                                // 리딤을 행 단위로 복원. LicenseDealerTab(3271513b) selectedNodeFacts /
                                // orgWideRedeemedForSelected 와 같은 함수·술어(하위 보유 + 노드 모델)로 게이트.
                                const rowHasDescendants = useNodeModel && hasAllocationDescendants(dealerId, allocationNodes);
                                const rowAllocated = rowHasDescendants
                                    ? extractAllocationFacts(allocationNodes?.[dealerId], dealerChildren)?.allocated
                                    : null;
                                const rowOrgWideRedeemed = rowHasDescendants
                                    ? sumSubtreeRedeemed(dealerId, allocationNodes)
                                    : null;
                                const remI = dealerQuota.initial.selfUsable;
                                const remR = dealerQuota.renewal.selfUsable;
                                return (
                                    <tr key={d.sk}>
                                        <td>
                                            {/* IMP-166: dealerId 부재(extractDealerIdFromSk null) 시 fallback "-" —
                                                기존 빈 div 대비 다른 리스트(AllocationHistory/CustomerLicenses)와 일관. */}
                                            <CompanyCell id={dealerId} companyMap={companyMap} fallback="-" />
                                        </td>
                                        <td>{resolveOrgTypeLabel(company?.org, t) || '-'}</td>
                                        <td>
                                            {d.t_initial ?? 0} / {d.t_renewal ?? 0}
                                            {rowAllocated && (
                                                <small className="text-muted d-block" data-testid={`license-subsidiary-row-allocated-${dealerId}`}>
                                                    Σ {t('license.status.allocated')} {rowAllocated.newQty ?? 0} / {rowAllocated.renewQty ?? 0}
                                                </small>
                                            )}
                                        </td>
                                        <td>
                                            {d.u_initial ?? 0} / {d.u_renewal ?? 0}
                                            {rowOrgWideRedeemed && (
                                                <small className="text-muted d-block" data-testid={`license-subsidiary-row-redeemed-orgwide-${dealerId}`}>
                                                    {t('license.label.redeemedOrgWide')} {rowOrgWideRedeemed.newQty ?? 0} / {rowOrgWideRedeemed.renewQty ?? 0}
                                                </small>
                                            )}
                                        </td>
                                        {/* Remaining 셀 — 헤더와 동일하게 강조: subtle 배경 + bold + primary 컬러. */}
                                        <td className="bg-primary-subtle fw-bold text-primary">
                                            {remI} / {remR}
                                        </td>
                                        {canEdit && (
                                            <td>
                                                {/* RTSQA-339 round 14 (사용자 발견 2026-05-14): list 액션
                                                    버튼 정렬 보정 — margin-left 1px. */}
                                                {/* IMP-425 §12.1-A: 자기 대상 할당/회수는 서버가 항상 거부
                                                    (SELF_ALLOCATION_MIDCORP / SELF_REVOKE_BLOCKED)하므로 자기 행에는
                                                    눌러도 실패하는 버튼을 노출하지 않는다. 행 자체는 표시 유지(캡션
                                                    Σ 항등식·레거시 셀프딜링 잔재 탐지). LicenseDealerTab(:318
                                                    !isSelfSelected)과 동일한 대칭 패턴. */}
                                                {dealerId !== user?.companyId && (
                                                    <>
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1 py-0"
                                                            style={{ fontSize: '0.75rem', margin: '0 0 0 1px' }}
                                                            onClick={() => setAllocateModal({ show: true, mode: 'allocate', record: d })}
                                                        >
                                                            {t('license.button.allocate')}
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger py-0"
                                                            style={{ fontSize: '0.75rem', margin: '0 0 0 1px' }}
                                                            onClick={() => setAllocateModal({ show: true, mode: 'revoke', record: d })}
                                                        >
                                                            {t('license.button.revoke')}
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

                            </>
                        ),
                    },
                    {
                        key: 'allocationHistory',
                        label: t('license.label.allocationHistory'),
                        content: (
                            <>
            {/* FEAT-024 AC-3 + AC-7 — 할당/회수 이력 + 기간 필터. 탭 라벨이 제목이므로 우측에 기간 필터만 둔다.
                Hide/Show 토글 없음 — 항상 표시. */}
            <div className={FILTER_ROW_CLASS}>
                <PeriodFilter value={historyFilter} onChange={setHistoryFilter} />
            </div>
            {/* HQ → Corp Grant History 섹션은 일상 운영과 무관하여 비노출.
                필요 시 향후 별도 admin-only 뷰로 이전 검토. */}
            <AllocationHistoryTable history={filteredDealerAllocationHistory} kind="dealer" loading={historyLoading} companyMap={companyMap} />
                            </>
                        ),
                    },
                    // 템플릿 편집기는 선택했을 때만 마운트(종전 구역 전환과 같은 조회 시점).
                    ...(canEditVoucherTemplate ? [{
                        key: TAB_TEMPLATE,
                        label: t('license.label.voucherTemplate'),
                        content: activeTab === TAB_TEMPLATE ? <VoucherTemplateEditor product={product} /> : null,
                    }] : []),
                ]}
            />

            {/* 모달 */}
            <AllocateDealerModal
                show={allocateModal.show}
                mode={allocateModal.mode}
                packageId={selectedPackage?._id}
                corpId={user?.companyId}
                product={product}
                existingRecord={allocateModal.record}
                onClose={() => setAllocateModal({ ...allocateModal, show: false })}
                onSuccess={() => {
                    setAllocateModal({ ...allocateModal, show: false });
                    // BUG-210 리뷰(Major): 조회 세트를 fetchAllocations 와 인라인으로 다시 나열하면
                    // 발산이 재발하므로, 같은 함수를 fresh:true 로 호출해 4개 전부(GetCorpAllocation/
                    // GetDealerAllocations/GetMyDealerAllocation/GetAllocationSubtree)를 한 곳에서 재조회한다
                    // (BUG-211: fresh:true 로 write-후 dedupe 캐시 우회).
                    fetchAllocations({ fresh: true });
                    // LicenseOverviewTab이 구독하는 LicenseReducer.licenses 동기화 (스펙 §3.3 Overview 탭 갱신)
                    dispatch(LicenseAction.GetLicenseByCompanyId(user.companyId));
                    // FEAT-024 AC-7 — 이력 항상 표시이므로 무조건 즉시 갱신
                    dispatch(SmartDentAction.GetDealerAllocationHistory(user.companyId, null, { product }));
                }}
            />
        </div>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps.product?.key);
    return {
        corpAllocation: licenseState.corpAllocation,
        dealerAllocations: licenseState.dealerAllocations,
        myReceivedAllocation: licenseState.myReceivedAllocation,
        dealerAllocationHistory: licenseState.dealerAllocationHistory,
        companies: state.CompanyReducer.companies,
        allocationNodes: licenseState.allocationNodes,
        scopeContext: licenseState.scopeContext,
    };
};

export default connect(mapState)(LicenseSubsidiaryTab);
